import React from "react";
import { Download, Share, SquarePlus, X } from "lucide-react";
import { cn } from "../../lib/cn";

// Remembers a dismissal so the sheet never nags. Cleared only by the user
// clearing site data, or by installing (at which point it is moot).
const DISMISS_KEY = "kbc-install-dismissed";
const OPEN_DELAY_MS = 1500;

const InstallContext = React.createContext({
  canInstall: false, isIOS: false, installed: true,
  openSheet: () => {}, install: () => {},
});

export const useInstall = () => React.useContext(InstallContext);

/** Already running as an installed app, so there is nothing to offer. */
function detectStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches === true ||
    // iOS Safari predates display-mode and uses its own flag.
    window.navigator.standalone === true
  );
}

/**
 * iPadOS 13+ reports itself as "Macintosh", so a plain userAgent test misses
 * every iPad. maxTouchPoints separates a real Mac (0) from an iPad (5).
 *
 * Deliberately not `"ontouchend" in document`: that is present in some desktop
 * Chromium builds regardless of hardware, which would show a Mac user iPhone
 * instructions and hide the Install button that would actually have worked.
 */
function detectIOS() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (ua.includes("Mac") && navigator.maxTouchPoints > 1);
}

/**
 * Only offer the install on phones and tablets.
 *
 * Desktop Chrome and Edge are perfectly capable of installing, and will fire
 * beforeinstallprompt, but a desktop user visiting a community directory is not
 * looking for a windowed copy of it, so the sheet is just an obstacle there.
 *
 * The UA test carries the decision, with the coarse-pointer clause as a
 * fallback for devices it does not name. Coarse pointer alone would be wrong:
 * it matches a desktop with a touchscreen, which is still a desktop. The width
 * bound keeps that case out.
 */
function detectMobileLike() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent;
  if (/Android|iPhone|iPad|iPod|Mobile|Silk|Kindle|Opera Mini|IEMobile/i.test(ua)) return true;
  if (detectIOS()) return true;
  const coarse = window.matchMedia?.("(pointer: coarse)").matches === true;
  return coarse && window.innerWidth <= 1024;
}

/**
 * Install affordance for the PWA.
 *
 * Android/Chrome/Edge get a real install: the browser fires
 * `beforeinstallprompt`, we suppress its own banner, keep the event, and
 * replay it when the user taps Install.
 *
 * iOS gets instructions and nothing more. Safari has never implemented
 * `beforeinstallprompt`, so there is no programmatic install on iPhone or iPad
 * from any framework. Share > Add to Home Screen is the only route, so the
 * honest thing is to show where that button is rather than a dead Install
 * button that silently does nothing.
 */
export function InstallProvider({ children }) {
  const [deferred, setDeferred] = React.useState(null);
  const [installed, setInstalled] = React.useState(detectStandalone);
  const [open, setOpen] = React.useState(false);
  const iOS = React.useMemo(detectIOS, []);
  const mobileLike = React.useMemo(detectMobileLike, []);

  const dismissedRef = React.useRef(false);
  React.useEffect(() => {
    try { dismissedRef.current = localStorage.getItem(DISMISS_KEY) === "1"; } catch { /* no-op */ }
  }, []);

  React.useEffect(() => {
    const onBeforeInstall = (e) => {
      // Without preventDefault Chrome may show its own mini-infobar as well.
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setOpen(false);
      setDeferred(null);
      try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* no-op */ }
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Auto-open on first visit. Deliberately keyed on `deferred` rather than a
  // bare mount timer: on Android `beforeinstallprompt` often arrives after
  // first paint, and opening before it lands would render an Install button
  // with nothing behind it.
  // Desktop is excluded outright: see detectMobileLike. On iOS there is no
  // event to wait for, so the instructions are all we can offer.
  const canOffer = installed || !mobileLike ? false : iOS || Boolean(deferred);
  React.useEffect(() => {
    if (!canOffer || dismissedRef.current) return undefined;
    const t = setTimeout(() => setOpen(true), OPEN_DELAY_MS);
    return () => clearTimeout(t);
  }, [canOffer]);

  const dismiss = React.useCallback(() => {
    setOpen(false);
    dismissedRef.current = true;
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* no-op */ }
  }, []);

  const install = React.useCallback(async () => {
    if (!deferred) return;
    // Must run inside a user gesture, which is why this is only ever reached
    // from a click handler and never from the auto-open timer.
    deferred.prompt();
    try {
      await deferred.userChoice;
    } finally {
      // A deferred prompt is single use; the browser will fire a fresh
      // beforeinstallprompt if the user declines and becomes eligible again.
      setDeferred(null);
      setOpen(false);
    }
  }, [deferred]);

  const openSheet = React.useCallback(() => setOpen(true), []);

  const value = React.useMemo(
    () => ({ canInstall: canOffer, isIOS: iOS, installed, openSheet, install }),
    [canOffer, iOS, installed, openSheet, install]
  );

  return (
    <InstallContext.Provider value={value}>
      {children}
      <InstallSheet open={open && canOffer} onDismiss={dismiss} onInstall={install} isIOS={iOS} />
    </InstallContext.Provider>
  );
}

function InstallSheet({ open, onDismiss, onInstall, isIOS }) {
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onDismiss(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onDismiss]);

  return (
    <>
      <div
        className={cn(
          "fixed inset-0 z-[60] bg-black/50 transition-opacity duration-200",
          open ? "visible opacity-100" : "invisible opacity-0"
        )}
        onClick={onDismiss}
        aria-hidden="true"
      />
      {/* Bottom sheet on phones, centred card from sm up. `invisible` when
          closed keeps its controls out of the tab order. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="install-title"
        className={cn(
          "fixed z-[70] transition-[transform,visibility,opacity] duration-200 ease-out",
          "inset-x-0 bottom-0 rounded-t-2xl",
          "sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:w-[420px] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-2xl",
          "border border-line/15 bg-surface p-5 shadow-2xl",
          open ? "visible translate-y-0 opacity-100" : "invisible translate-y-full opacity-0 sm:translate-y-[-45%]"
        )}
      >
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Not now"
          className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-md text-muted transition hover:bg-line/5 hover:text-ink"
        >
          <X size={16} />
        </button>

        <div className="flex items-start gap-3 pr-8">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary font-display text-sm font-bold text-onprimary">
            KB
          </span>
          <div>
            <h2 id="install-title" className="font-display text-base font-bold text-ink">
              Install KBC Directory
            </h2>
            <p className="mt-0.5 text-sm text-muted">
              Add it to your home screen for full-screen access and faster loading.
            </p>
          </div>
        </div>

        {isIOS ? (
          <div className="mt-4 rounded-lg border border-line/15 bg-surface2 p-3">
            <p className="text-sm font-medium text-ink">To install on iPhone or iPad:</p>
            <ol className="mt-2 space-y-2 text-sm text-text">
              <li className="flex items-center gap-2">
                <Share size={16} className="shrink-0 text-primary" />
                <span>Tap <strong className="text-ink">Share</strong> in the Safari toolbar</span>
              </li>
              <li className="flex items-center gap-2">
                <SquarePlus size={16} className="shrink-0 text-primary" />
                <span>Choose <strong className="text-ink">Add to Home Screen</strong></span>
              </li>
            </ol>
          </div>
        ) : null}

        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={onDismiss}
            className="kbc-control flex-1 rounded-md border border-line/15 px-3 py-2 text-sm font-medium text-text transition hover:bg-line/5 hover:text-ink"
          >
            Not now
          </button>
          {!isIOS && (
            <button
              type="button"
              onClick={onInstall}
              className="kbc-control flex-1 inline-flex items-center justify-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-onprimary transition hover:opacity-90"
            >
              <Download size={15} />
              Install
            </button>
          )}
        </div>
      </div>
    </>
  );
}

/**
 * Header affordance for anyone who dismissed the sheet. Renders nothing when
 * the app is already installed or the browser cannot offer an install.
 */
export function InstallButton({ className }) {
  const { canInstall, isIOS, install, openSheet } = useInstall();
  if (!canInstall) return null;
  return (
    <button
      type="button"
      // Android can install straight from the gesture. iOS has no programmatic
      // install, so the only useful action is to show the instructions.
      onClick={isIOS ? openSheet : install}
      title="Install app"
      aria-label="Install app"
      className={cn(
        "kbc-control inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-xs font-medium text-text ring-1 ring-inset ring-line/15 transition-colors duration-150 hover:bg-line/5 hover:text-ink",
        className
      )}
    >
      <Download size={15} />
      <span className="hidden sm:inline">Install</span>
    </button>
  );
}

export default InstallProvider;
